import { HttpEventType } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as geojson from 'geojson';
import { Feature } from 'geojson';
import { FileInput } from 'ngx-material-file-input';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppError } from 'src/app/core/errors/http-error/app-error';
import { LocationType } from 'src/app/shared/components/location-search/location-type.enum';
import { MapService } from 'src/app/shared/components/map/map.service';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { createProject, Project, ProjectUploadStatus } from '../state/project.model';
import { ProjectQuery } from '../state/project.query';
import { ProjectService } from '../state/project.service';

@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.scss']
})
export class ProjectFormComponent implements OnInit {

  projectForm: FormGroup;
  searchResult$: Observable<geojson.Feature<geojson.Geometry, { [name: string]: any; }>[]>;

  error: AppError;
  progress: number;
  locationFeature: Feature;

  locationTypes = [
    LocationType.ADDRESS,
    LocationType.DISTRICT,
    LocationType.LOCALITY,
    LocationType.NEIGHBORHOOD,
    LocationType.PLACE,
    LocationType.POSTCODE,
    LocationType.REGION
  ];

  editionMode = false;
  uploadError: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public project: Project,
    private fb: FormBuilder,
    private projectService: ProjectService,
    private projectQuery: ProjectQuery,
    private windowService: WindowService,
    private mapService: MapService
  ) {
    if (project && project.id) {
      this.editionMode = true;
      this.uploadError = this.projectQuery.getUi(project.id).uploadStatus === ProjectUploadStatus.Error;
    }
  }

  ngOnInit(): void {
    this.projectForm = this.fb.group({
      name: [this.project ? this.project.name : '', Validators.required],
      description: [this.project ? this.project.description : '', Validators.required]
    });

    if (!this.editionMode || this.uploadError) {
      this.projectForm.addControl('file',
        this.fb.control('', [Validators.required, zipFileValidator])
      );
    }
  }

  save() {
    this.loadFromForm();
    if (this.project.id) {
      this.update();
    } else {
      this.create();
    }
  }

  private update() {
    this.projectService.update(this.project.id, this.project)
      .pipe(
        catchError(error => {
          this.error = error;
          this.projectForm.enable();
          return throwError(error);
        })
      )
      .subscribe((p: Project) => {
        if (this.uploadError) {
          this.project = p;
          this.upload();
        } else {
          this.windowService.close(this);
        }
      });
  }

  private create() {
    this.navegateTo(this.locationFeature);
    this.projectService.add(this.project)
      .pipe(
        catchError(error => {
          this.error = error;
          this.projectForm.enable();
          return throwError(error);
        })
      )
      .subscribe((p: Project) => {
        this.project = p;
        this.upload();
      });
  }

  private loadFromForm() {
    this.projectForm.disable();
    this.project = createProject(this.project);
    this.project.name = this.projectForm.get('name').value as string;
    this.project.description = this.projectForm.get('description').value as string;
  }

  private upload() {
    this.progress = 1;

    const file = this.projectForm.get('file').value;

    this.projectService.upload(this.project.id, file.files[0]).pipe(
      map((event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.progress = Math.round((100 / event.total) * event.loaded);
        } else if (event.type === HttpEventType.Response) {
          this.progress = null;
          this.projectService.updateUI(this.project.id, { uploadStatus: ProjectUploadStatus.Waiting });
          this.projectService.setActive(this.project.id);
          this.windowService.close(this);
        }
      }),
      catchError((err: any) => {
        this.progress = null;
        this.projectForm.enable();
        return throwError(err.message);
      })
    )
      .subscribe();
  }

  private navegateTo(feature: any) {
    if (!feature.bbox) {
      return;
    }
    this.mapService.map$.subscribe(m => m.fitBounds(feature.bbox));
  }

}

const zipFileValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const file: FileInput = control.value;

  if (!file) {
    return null;
  }

  if (!file.fileNames.endsWith('.zip')) {
    return { zipFileValidator: true };
  }
  return null;
};
